// Strategy Performance — simplified 7-column read-only view (2026-06).
//
// One row per (display_strategy, symbol). Closed-trade only. Open
// positions live on the live dashboard / LBank reconciliation page
// — they MUST NOT appear here or inflate the trade count.
//
// Source: GET /api/strategy-performance → response.summary (the
// new operator-facing aggregate). The legacy `groups` field is
// still returned by the API for backward compat but ignored here.
//
// Family filter buttons match by display_strategy prefix:
//   V2   → "BTC V2"  (legacy_v2 / V2 strategy_ids merge into this)
//   BOF  → "BOF_*"
//   MDIV → "MDIV_*"
import { useEffect, useMemo, useState } from "react";
import { api, fmtUsd } from "@/lib/api";
import { SectionTitle } from "@/components/Bits";
import { BarChart3 } from "lucide-react";

const FAMILIES = [
  { value: "",     label: "All" },
  { value: "V2",   label: "V2" },
  { value: "MDIV", label: "MDIV" },
  { value: "BOF",  label: "BOF" },
];

const POLL_MS = 15_000;

function matchesFamily(displayStrategy, family) {
  if (!family) return true;
  const s = (displayStrategy || "").toUpperCase();
  if (family === "V2")   return s === "BTC V2" || s.startsWith("V2");
  if (family === "MDIV") return s.startsWith("MDIV");
  if (family === "BOF")  return s.startsWith("BOF");
  return true;
}

function fmtPF(v) {
  if (v === "inf" || v === Infinity) return "∞";
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(2);
}

function fmtWR(v) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(1)}%`;
}

function pnlClass(v) {
  if (v == null) return "text-[#A0A0AB]";
  if (v > 0) return "text-[#00E676]";
  if (v < 0) return "text-[#FF5252]";
  return "text-[#A0A0AB]";
}

export default function StrategyTrades() {
  const [summary, setSummary] = useState([]);
  const [family, setFamily] = useState("");
  const [symbolFilter, setSymbolFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const reload = async () => {
    try {
      const out = await api.strategyPerformance({
        symbol: symbolFilter || undefined,
      });
      setSummary(Array.isArray(out.summary) ? out.summary : []);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;
    const tick = async () => { if (alive) await reload(); };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { alive = false; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbolFilter]);

  const visible = useMemo(() => {
    return summary.filter((g) => matchesFamily(g.display_strategy, family));
  }, [summary, family]);

  const totals = useMemo(() => {
    return visible.reduce(
      (acc, g) => {
        acc.trades += g.trades || 0;
        acc.pnl    += Number(g.pnl) || 0;
        return acc;
      },
      { trades: 0, pnl: 0 },
    );
  }, [visible]);

  return (
    <div className="space-y-6" data-testid="strategy-trades-page">
      <SectionTitle sub="One row per (strategy, symbol). Closed-trade history only. Read-only; never gates execution.">
        <span className="inline-flex items-center gap-2">
          <BarChart3 size={20} className="text-[#A0A0AB]" />
          Strategy Performance
        </span>
      </SectionTitle>

      {err && (
        <div
          className="bg-[#FF5252]/10 border border-[#FF5252]/30 text-[#FF5252] rounded-sm p-3 text-sm"
          data-testid="strategy-trades-error"
        >
          {err}
        </div>
      )}

      {/* Filters */}
      <div
        className="flex flex-wrap items-center gap-3"
        data-testid="strategy-trades-filters"
      >
        <div className="text-xs text-[#71717A] uppercase tracking-widest">Family</div>
        <div className="inline-flex rounded-sm overflow-hidden border border-border">
          {FAMILIES.map((f) => (
            <button
              key={f.value || "all"}
              type="button"
              data-testid={`perf-family-${f.value || "all"}`}
              onClick={() => setFamily(f.value)}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider ${
                family === f.value
                  ? "bg-[#1F1F2A] text-[#E4E4E7]"
                  : "bg-[#0F0F14] text-[#71717A] hover:text-[#E4E4E7]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <input
          type="text"
          value={symbolFilter}
          onChange={(e) => setSymbolFilter(e.target.value.toUpperCase())}
          placeholder="symbol… (e.g. BTCUSDT)"
          data-testid="perf-symbol-filter"
          className="bg-[#0F0F14] border border-border rounded-sm px-3 py-1.5 text-sm font-mono w-56"
        />
        {symbolFilter && (
          <button
            type="button"
            onClick={() => setSymbolFilter("")}
            data-testid="perf-clear-symbol"
            className="text-xs text-[#71717A] hover:text-[#E4E4E7]"
          >clear</button>
        )}

        <div className="ml-auto text-xs font-mono text-[#71717A]">
          {visible.length} row{visible.length !== 1 ? "s" : ""} ·{" "}
          {totals.trades} closed trades ·{" "}
          Σ PnL <span className={pnlClass(totals.pnl)}>{fmtUsd(totals.pnl || 0)}</span>
        </div>
      </div>

      {/* Strategy Performance table — exactly 7 operator-spec columns */}
      <div className="bg-[#0F0F14] border border-border rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table
            className="w-full text-sm"
            data-testid="strategy-performance-table"
          >
            <thead className="bg-[#1A1A22] text-[#71717A] text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left  px-3 py-2" data-testid="perf-col-strategy">Strategy</th>
                <th className="text-left  px-3 py-2" data-testid="perf-col-symbol">Symbol</th>
                <th className="text-right px-3 py-2" data-testid="perf-col-trades">Trades</th>
                <th className="text-right px-3 py-2" data-testid="perf-col-win-rate">Win Rate</th>
                <th className="text-right px-3 py-2"
                    title="gross_profit / |gross_loss| of closed trades"
                    data-testid="perf-col-pf">Profit Factor</th>
                <th className="text-right px-3 py-2" data-testid="perf-col-pnl">PnL</th>
                <th className="text-right px-3 py-2"
                    title="Peak-to-trough drawdown of the closed-trade equity curve"
                    data-testid="perf-col-max-dd">Max DD</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {visible.length === 0 && !loading ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-8 text-[#71717A] text-center"
                    data-testid="strategy-performance-empty"
                  >
                    No closed trades recorded yet for this filter.
                  </td>
                </tr>
              ) : null}
              {visible.map((g) => {
                const key = `${g.display_strategy}::${g.symbol}`;
                return (
                  <tr
                    key={key}
                    className="border-t border-border"
                    data-testid={`perf-row-${g.display_strategy.replace(/\s+/g, "_")}-${g.symbol}`}
                  >
                    <td className="px-3 py-2 text-[#E4E4E7]">{g.display_strategy}</td>
                    <td className="px-3 py-2 text-[#A0A0AB]">{g.symbol}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{g.trades}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtWR(g.win_rate)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtPF(g.profit_factor)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${pnlClass(g.pnl)}`}>
                      {g.pnl == null ? "—" : fmtUsd(g.pnl)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-[#FF5252]">
                      {g.max_dd == null ? "—" : fmtUsd(g.max_dd)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
